/**
 * Checkout API — booking initiation + the demo mock-checkout flow used by
 * the embed widget.
 *
 * Endpoints:
 *   POST /partners/v1/embedded/mock-checkout      (unauthenticated)
 *   POST /chat/v1/{sessionId}/book/{itemId}       (session-authed proxy)
 *
 * Functions take an injected axios instance so each app supplies its own
 * auth wiring — the embed passes its instance-scoped clients, never a
 * baked-in singleton.
 */
import type { AxiosInstance } from 'axios';
import type { BookingInitRequest, BookingInitResponse } from '@wg/types';

// ============================================================================
// Mock checkout (demo flow — no payment processor, no real provider booking)
// ============================================================================

export interface MockCheckoutContact {
  name?: string;
  name_first?: string;
  name_last?: string;
  email?: string;
  phone?: string;
}

export interface MockCheckoutGuestRow {
  type: string;
  count: number;
  /** Provider-specific tier id (e.g. PonoRez price-tier int). Threaded
   * through so the URL builder can emit `guests_t<TIER>=N` without a
   * downstream lookup. */
  tier_id?: string | number;
}

export interface MockCheckoutLineItem {
  activity_name: string;
  activity_id?: string;
  image_url?: string;
  date?: string;
  guests?: MockCheckoutGuestRow[];
  unit_price_cents?: number;
}

export interface MockCheckoutRequest {
  public_token: string;
  session_id?: string | null;
  parent_origin?: string | null;
  contact: MockCheckoutContact;
  line_item: MockCheckoutLineItem;
  total_cents: number;
  currency_code?: string;
  provider?: string;
  external_supplier_id?: string;
}

export interface MockCheckoutResponse {
  booking_id: string;
  confirmation_code: string;
  status: string;
  created_at: string;
}

/**
 * Submit a faux order to functions-py and get back a confirmation code.
 * Pass the UNAUTHENTICATED client — the endpoint authenticates via the
 * instance public_token in the body, not a user JWT.
 */
export async function submitMockCheckout(
  http: AxiosInstance,
  body: MockCheckoutRequest,
): Promise<MockCheckoutResponse> {
  const { data } = await http.post<MockCheckoutResponse>(
    '/partners/v1/embedded/mock-checkout',
    body,
  );
  return data;
}

// ============================================================================
// Booking initiation (session-scoped proxy)
// ============================================================================

/**
 * Body fields the embed widget passes. Excludes `experience_id` (passed
 * in the URL), `surface` (always 'embed'), and `container` (always
 * 'iframe') — the proxy stamps those server-side.
 */
export type EmbedBookingInitBody = Omit<
  BookingInitRequest,
  'experience_id' | 'surface' | 'container'
> & {
  container?: 'iframe' | 'webview' | 'redirect';
};

/**
 * Provider-agnostic booking initiator. Wraps the chat-router's
 * authenticated proxy to /experiences/v1/{item_id}/book — the canonical
 * public booking endpoint that RN, the main webapp, and the brain all use.
 *
 * Why the proxy: the embed visitor is session-bound (not user-bound), so
 * the browser can't carry a user JWT. livekit-api resolves the session's
 * principal (instance, partner, public-token suffix) and forwards
 * server-to-server to functions-py with the internal apikey path.
 *
 * Provider keys come in two flavors:
 *   - `itemId` as a catalog UUID (first-party or registered providers)
 *   - `itemId` as a provider slug (`viator_5537320P1`, `ponorez_387:9123`)
 * Both resolve server-side. Embed seed cards usually hold the slug.
 *
 * Replaces the older buildCheckoutForSession path that hit
 * /chat/v1/{sid}/checkout-build → /catalog/v1/checkout-build. /catalog/v1/*
 * is now internal-only; /experiences/v1/* is the single public surface.
 */
export async function initiateBookingForSession(
  http: AxiosInstance,
  sessionId: string,
  itemId: string,
  body: EmbedBookingInitBody,
): Promise<BookingInitResponse> {
  const { data } = await http.post<BookingInitResponse>(
    `/chat/v1/${sessionId}/book/${encodeURIComponent(itemId)}`,
    body,
  );
  return data;
}

/** Tracking-session lifecycle as the booking sync sees it. */
export type TrackingSessionStatus =
  | 'initiated'
  | 'likely_booked'
  | 'confirmed'
  | 'cancelled'
  | 'expired'
  | 'abandoned';

export interface TrackingSessionSnapshot {
  id: string;
  status: TrackingSessionStatus;
  provider?: string | null;
  possible_confirmation_code?: string | null;
  confirmation_code?: string | null;
  likely_booked_at?: string | null;
  confirmed_at?: string | null;
}

/**
 * Poll a tracking session, session-scoped. Used after the visitor is handed
 * off to a partner site we cannot frame: the panel stays on a "waiting on
 * your booking" surface and flips to confirmed when the provider booking
 * sync reconciles the session server-side.
 */
export async function getBookingStatusForSession(
  http: AxiosInstance,
  sessionId: string,
  trackingSessionId: string,
): Promise<TrackingSessionSnapshot | null> {
  const { data } = await http.get<{ session: TrackingSessionSnapshot | null }>(
    `/chat/v1/${sessionId}/booking-status/${encodeURIComponent(
      trackingSessionId,
    )}`,
  );
  return data?.session ?? null;
}

/**
 * Tell the backend the visitor says they completed the booking on the
 * partner tab. Marks the session likely_booked (and its itinerary item
 * pending_confirmation) so the sync can reconcile it. `detected_url` is
 * deliberately omitted — a null there is what distinguishes a self-reported
 * completion from one the iframe inspector actually observed.
 */
export async function reportLikelyBookedForSession(
  http: AxiosInstance,
  sessionId: string,
  trackingSessionId: string,
): Promise<TrackingSessionSnapshot | null> {
  const { data } = await http.post<{ session: TrackingSessionSnapshot | null }>(
    `/chat/v1/${sessionId}/likely-booked/${encodeURIComponent(
      trackingSessionId,
    )}`,
    {},
  );
  return data?.session ?? null;
}
